import { LanguageType } from '@/models/LanguageType';
import { OnChangeProps } from '@/models/typings/ReactDateRange';
import { WorkshopMeeting } from '@/models/WorkshopMeeting';
import DatePicker from '@/pages/Contents/LMS/Classes/DatePicker';
import { currentDate, getNewDate, resolveLocale } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { LocaleData } from '@/translations/LocaleData';
import { isBefore } from 'date-fns';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { DateFieldNames } from '..';

interface Props {
    dateFieldName?: DateFieldNames;
    startDate?: Date;
    finishDate?: Date;
    meetings?: WorkshopMeeting[];
    onHide: () => void;
    onChange: (value: OnChangeProps) => void;
}

const DatePickerPeriod: React.FC<Props> = ({ dateFieldName, startDate, finishDate, meetings = [], onHide, onChange }) => {
    const strings: LocaleData = useSelector((state: ApplicationState) => state.locale.data);

    const appLocale: LanguageType = useSelector((state: ApplicationState) => state.locale.data._LANGUAGE);

    const colors: string[] = useSelector((state: ApplicationState) => state.appOrganization.data.colors);

    const initialDate = useMemo((): Date | undefined => {
        if (dateFieldName === DateFieldNames.startDate) {
            return startDate;
        }

        if (dateFieldName === DateFieldNames.finishDate) {
            return finishDate;
        }

        return undefined;
    }, [dateFieldName, startDate, finishDate]);

    const minDate = useMemo((): Date | undefined => {
        if (dateFieldName === DateFieldNames.startDate) {
            return currentDate();
        }

        if (dateFieldName === DateFieldNames.finishDate && meetings.length > 0) {
            const times: number[] = meetings.reduce((finishDateTimes: number[], meeting: WorkshopMeeting) => {
                if (meeting.endDate) {
                    finishDateTimes.push(new Date(meeting.endDate).getTime());
                }

                return finishDateTimes;
            }, []);

            if (times.length > 0) {
                return getNewDate(Math.max(...times));
            }
        }

        if (startDate && isBefore(startDate, currentDate())) {
            return currentDate();
        }

        return startDate;
    }, [dateFieldName, startDate, meetings]);

    const maxDate = useMemo((): Date | undefined => {
        let date: Date | undefined;

        if (dateFieldName === DateFieldNames.startDate) {
            if (finishDate) {
                date = finishDate;
            }

            if (meetings.length > 0) {
                const times: number[] = meetings.reduce((startDateTimes: number[], meeting: WorkshopMeeting) => {
                    if (meeting.startDate) {
                        startDateTimes.push(new Date(meeting.startDate).getTime());
                    }

                    return startDateTimes;
                }, []);

                if (times.length > 0) {
                    date = getNewDate(Math.min(...times));
                }
            }
        }

        return date;
    }, [dateFieldName, finishDate]);

    return (
        <DatePicker
            color={colors[0]}
            initialDate={initialDate}
            locale={resolveLocale(appLocale)}
            maxDate={maxDate}
            minDate={minDate}
            onChange={onChange}
            onHide={onHide}
            show={dateFieldName !== undefined}
            title={dateFieldName === DateFieldNames.startDate ? strings.START_DATE : strings.END_DATE}
            weekdayDisplayFormat={'EEEEEE'}
        />
    );
};

export { DatePickerPeriod };
